import { RingFinishEnum } from '@jouzen/ecom-components/v3/enums/ringFinish';
import type { RingFinish } from '@jouzen/ecom-components/v3/types/ringFinish';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import type { PageParams } from '@/app/[locale]/store/rings/oura-ring-4/(pdp)/_types/pageParams';
import { STORE_POP_PATHNAME } from '@/app/consts/pathnames';

import type { RingPDPRoutePrefix } from './types';

export interface Finish {
  isValid: boolean;
  value: RingFinish | '';
}

export interface CurrentRouteProps {
  locale: PageParams['locale'];
  routePrefix: RingPDPRoutePrefix;
}

export interface CurrentRouteValues {
  finish: Finish;
  isReady: boolean;
  pushFinish: (value: RingFinish) => void;
}

const EXCLUDED_SEGMENTS = ['', 'store', 'product', 'rings'];

const useCurrentRoute = ({
  locale,
  routePrefix,
}: CurrentRouteProps): CurrentRouteValues => {
  const pathname = usePathname();
  const segments = pathname
    .split('/')
    .filter((segment) => ![...EXCLUDED_SEGMENTS, locale].includes(segment));
  const routes = useMemo(() => ({ ...segments }), [segments]);

  // check finish
  const finish = useMemo(() => {
    const finishValue = routes[1] ?? '';
    const value = finishValue as RingFinish;
    // valid if it's in the enum of finishes for OR4/OR4C
    const isValid = Object.values(RingFinishEnum).includes(value);

    return {
      value,
      isValid,
    };
  }, [routes]);

  // Helper function for consistent options
  const pushRing = useCallback((subpath: string) => {
    window.history.pushState(null, '', `${STORE_POP_PATHNAME}/${subpath}`);
  }, []);

  const pushFinish = useCallback(
    (value: RingFinish) => {
      if (value !== finish.value) {
        pushRing(`${routePrefix}/${value}`);
      }
    },
    [finish.value, pushRing, routePrefix],
  );

  // used to hide any page flicker while the route gets sorted out
  const isReady = finish.isValid;

  return {
    isReady,
    finish,
    pushFinish,
  };
};

export default useCurrentRoute;
